import { PurchaseType, type PurchaseLicenseModalData } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';
import type { License } from '~/types/person/person';
import { checkoutAssignUrl, checkoutPurchaseUrl, checkoutUpgradeUrl } from '~/utils/cross-app-urls';
import { usePersonStore } from '~/stores/person';

export const useLicensePurchase = () => {
    const authStore = useAuthStore();
    const personStore = usePersonStore();
    const { showWarning } = useGlobalToast();
    const route = useRoute();

    // The hive path checkout should return to. Strips the one-shot trigger params (?upgrade /
    // ?purchase / ?subscribe) that a page uses to auto-open this purchase flow — otherwise the
    // return_to would point back at a URL that immediately re-fires the trigger and bounces the
    // user into checkout again. Centralised here so every entry point is covered without
    // per-caller cleanup.
    const buildReturnTo = (): string => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(route.query)) {
            if (key === 'upgrade' || key === 'purchase' || key === 'subscribe' || value == null) continue;
            if (Array.isArray(value)) {
                value.forEach((v) => v != null && params.append(key, String(v)));
            } else {
                params.append(key, String(value));
            }
        }
        const query = params.toString();
        return query ? `${route.path}?${query}` : route.path;
    };

    const canPurchaseLicense = computed(() => {
        const employer = authStore.user?.employer;
        if (!employer) return true;
        return employer.can_purchase_pro === true;
    });

    // Employer purchase-restriction guard, shared by every purchase entry point (including the
    // ones that build their own checkout redirect and therefore can't go through openPurchaseModal).
    // Returns false when the employer has disabled purchases; explicit `false` is silent, an
    // absent/other value surfaces the warning — preserving the original openPurchaseModal behaviour.
    const ensureCanPurchase = (): boolean => {
        if (authStore.user?.employer?.can_purchase_pro === false) {
            return false;
        }
        if (!canPurchaseLicense.value) {
            showWarning(
                'Purchase Not Available',
                'Your employer has disabled license purchases for employees. Please contact your company administrator and request a license.'
            );
            return false;
        }
        return true;
    };

    // Redirect to checkout to buy a NEW license for the currently-loaded person. When viewing a
    // managed/impersonated person, pre-target the new license to them (checkoutAssignUrl →
    // assigned_to_id) so checkout can't default it to the authenticated buyer; plain self-purchase
    // otherwise. Raw redirect (no guard) shared by the purchase entry points below.
    const redirectNewPurchaseForCurrentPerson = () => {
        const beneficiaryId = personStore.impersonating;
        const target = personStore.person;
        if (beneficiaryId && target) {
            const name = `${target.first_name ?? ''} ${target.last_name ?? ''}`.trim();
            window.location.href = checkoutAssignUrl({ id: target.id, name }, buildReturnTo());
            return;
        }
        window.location.href = checkoutPurchaseUrl(buildReturnTo());
    };

    // A license the loaded person owns as a person (owner.type === 1, owned by them) — the only
    // kind that can enter checkout's modify/upgrade flow. Employer/company-owned (owner.type !== 1)
    // or another person's license is managed elsewhere and 403s in checkout's change endpoints, so
    // it must never be sent there.
    const isModifiableByLoadedPerson = (license?: License | null): boolean => {
        if (!license) return false;
        return license.owner?.type === 1 && license.owner?.id === personStore.person?.id;
    };

    // Upgradeable = modifiable AND has a higher tier to move to (signature is the top tier, so it's
    // excluded here). The billing "Modify License" flow instead allows any owned tier — a signature
    // license can still be downgraded / term-changed — so it gates on isModifiableByLoadedPerson.
    const isUpgradeableLicense = (license?: License | null): boolean => {
        if (!license) return false;
        const isSignature = license.type?.toLowerCase() === 'signature';
        return !isSignature && isModifiableByLoadedPerson(license);
    };

    // Start a new-license purchase for the currently-loaded person, applying the employer guard.
    // Use this for entry points that buy "for whoever is on screen".
    const purchaseForCurrentPerson = () => {
        if (!ensureCanPurchase()) return;
        redirectNewPurchaseForCurrentPerson();
    };

    const openPurchaseModal = (data: PurchaseLicenseModalData = { type: PurchaseType.PURCHASE }) => {
        if (!ensureCanPurchase()) return;

        // REFUND still uses the legacy PurchaseLicensesModal (different surface entirely).
        if (data.type === PurchaseType.REFUND) {
            useDynamicDialog(ModalNames.PURCHASE_LICENSES, data);
            return;
        }

        // UPGRADE: send the license into checkout's modify flow only when it's actually modifiable
        // by the loaded person (owned + non-signature); otherwise a non-owned/gifted license id would
        // be pushed into modify. Fall back to a new purchase for the person on screen.
        if (data.type === PurchaseType.UPGRADE) {
            if (data.license?.id && isUpgradeableLicense(data.license)) {
                window.location.href = checkoutUpgradeUrl(data.license.id, buildReturnTo());
                return;
            }
            redirectNewPurchaseForCurrentPerson();
            return;
        }

        // GIFT (assign a license to a family member): if the user already owns an unassigned,
        // non-expired license, open the in-hive picker to assign one directly (or buy another).
        // With no spare license, go straight to checkout pre-targeted to that contact so the new
        // license auto-assigns to them on purchase.
        if (data.type === PurchaseType.GIFT && data.contact) {
            const hasAvailable = (personStore.person?.licenses ?? []).some(
                (l) => !l.assigned_to && !l.expired,
            );
            if (hasAvailable) {
                useDynamicDialog(ModalNames.GIVE_LICENSE, { contact: data.contact });
                return;
            }
            window.location.href = checkoutAssignUrl({ id: data.contact.id, name: data.contact.name }, buildReturnTo());
            return;
        }

        // PURCHASE or unknown → checkout select-plan modal (skips the marketing pricing page).
        // When a caller targets a specific person (data.for) and we're viewing that person under
        // impersonation, pre-target the new license to them so it can't default to the buyer.
        // Callers with no target (e.g. buying unassigned managed licenses) stay a plain purchase.
        if (data.for && personStore.impersonating === data.for) {
            redirectNewPurchaseForCurrentPerson();
            return;
        }
        window.location.href = checkoutPurchaseUrl(buildReturnTo());
    };

    return {
        canPurchaseLicense,
        ensureCanPurchase,
        isModifiableByLoadedPerson,
        openPurchaseModal,
        purchaseForCurrentPerson,
    };
};
